import React from 'react';
import { User } from '../types';
import { Home, Plus, FileText, Scan, Search, ChevronLeft, ChevronRight } from 'lucide-react';

interface SidebarProps {
  user?: User | null;
  onHome?: () => void;
  onNewChat?: () => void;
  onTreatmentPlan?: () => void;
  onScanAnalysis?: () => void;
  onDeepResearch?: () => void;
  onTreatmentDemo?: () => void;
  onNewSession?: () => void;
  onLoadSession?: (sessionId: string, messages: any[]) => void;
  currentSessionId?: string | null;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onShowTreatmentDemo?: () => void;
  onShowScanHistory?: () => void;
  onShowScanAnalysis?: () => void;
  onShowDeepResearch?: () => void;
  onShowAskQuestions?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  user, 
  onHome,
  onNewChat, 
  onTreatmentPlan,
  onScanAnalysis, 
  onDeepResearch, 
  onTreatmentDemo,
  onNewSession,
  onLoadSession,
  currentSessionId,
  isCollapsed = false,
  onToggleCollapse,
  onShowTreatmentDemo,
  onShowScanHistory,
  onShowScanAnalysis,
  onShowDeepResearch,
  onShowAskQuestions
}) => {
  // ========================================
  // FEATURE CONFIGURATION
  // Toggle features on/off by setting to true/false
  // ========================================
  const featureConfig = {
    showHome: true,           // Home button
    showAskQuestions: true,   // Ask Questions button
    showTreatmentPlan: true,  // Treatment Plan button
    showSmartScan: true,      // Smart Scan button
    showDeepResearch: false,  // Deep Research button (HIDDEN)
    showTreatmentDemo: true,  // Treatment Demo button (green)
  };

  // Use the new props if old ones aren't provided
  const handleNewChat = onShowAskQuestions || onNewChat || onNewSession;
  const handleTreatmentPlan = onTreatmentPlan || onShowTreatmentDemo;
  const handleScanAnalysis = onScanAnalysis || onShowScanAnalysis;
  const handleDeepResearch = onDeepResearch || onShowDeepResearch;
  const handleHome = onHome || (() => window.location.reload());
  const handleTreatmentDemo = onTreatmentDemo;

  if (isCollapsed) {
    return (
      <div className="w-16 bg-white border-r border-medical-200 flex flex-col h-full">
        {/* Collapsed Logo */}
        <div className="h-[73px] border-b border-medical-200 flex items-center justify-center">
          <img 
            src="/logo_small.png" 
            alt="OrthoMind Logo" 
            className="w-8 h-8 cursor-pointer"
            onClick={() => window.open('https://www.orthomind.ai/', '_blank')}
          />
        </div>
        
        {/* Collapse Toggle Button */}
        <button
          onClick={onToggleCollapse}
          className="p-2 hover:bg-medical-100 transition-colors flex items-center justify-center"
          title="Expand sidebar"
        >
          <ChevronRight className="w-5 h-5 text-medical-600" />
        </button>
        
        {/* Collapsed Buttons */}
        <div className="flex-1 p-2 space-y-2">
          {featureConfig.showHome && (
            <button
              onClick={handleHome}
              className="w-full p-3 hover:bg-medical-100 rounded-lg transition-colors flex items-center justify-center"
              title="Home"
            >
              <Home className="w-5 h-5 text-medical-600" />
            </button>
          )}
          
          {featureConfig.showAskQuestions && (
            <button
              onClick={handleNewChat}
              className="w-full p-3 hover:bg-medical-100 rounded-lg transition-colors flex items-center justify-center"
              title="Ask Questions"
            >
              <Plus className="w-5 h-5 text-medical-600" />
            </button>
          )}
          
          {featureConfig.showTreatmentPlan && (
            <button
              onClick={handleTreatmentPlan}
              className="w-full p-3 hover:bg-medical-100 rounded-lg transition-colors flex items-center justify-center"
              title="Treatment Plan"
            >
              <FileText className="w-5 h-5 text-medical-600" />
            </button>
          )}
          
          {featureConfig.showSmartScan && (
            <button
              onClick={handleScanAnalysis}
              className="w-full p-3 hover:bg-medical-100 rounded-lg transition-colors flex items-center justify-center"
              title="Smart Scan"
            >
              <Scan className="w-5 h-5 text-medical-600" />
            </button>
          )}
          
          {featureConfig.showDeepResearch && handleDeepResearch && (
            <button
              onClick={handleDeepResearch}
              className="w-full p-3 hover:bg-medical-100 rounded-lg transition-colors flex items-center justify-center"
              title="Deep Research"
            >
              <Search className="w-5 h-5 text-medical-600" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 bg-white border-r border-medical-200 flex flex-col h-full">
      {/* Logo with Collapse Button */}
      <div className="h-[73px] px-4 border-b border-medical-200 flex items-center">
        <div className="flex items-center justify-between w-full">
          <a 
            href="https://www.orthomind.ai/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
          >
            <img 
              src="/logo_small.png" 
              alt="OrthoMind Logo" 
              className="w-10 h-10 cursor-pointer"
            />
            <div>
              <h2 className="text-lg font-semibold text-medical-900">ORTHOMIND</h2>
            </div>
          </a>
          
          {/* Collapse Button */}
          <button
            onClick={onToggleCollapse}
            className="p-1.5 hover:bg-medical-100 rounded-lg transition-colors"
            title="Collapse sidebar"
          >
            <ChevronLeft className="w-4 h-4 text-medical-600" />
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="p-4 border-b border-medical-200 space-y-3">
        {/* Home Button */}
        {featureConfig.showHome && (
          <button
            onClick={handleHome}
            className="w-full medical-button flex items-center justify-center space-x-2"
          >
            <Home className="w-4 h-4" />
            <span>Home</span>
          </button>
        )}
        
        {/* Ask Questions Button */}
        {featureConfig.showAskQuestions && (
          <button
            onClick={handleNewChat}
            className="w-full medical-button-secondary flex items-center justify-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ask Questions</span>
          </button>
        )}
        
        {/* Treatment Plan Button */}
        {featureConfig.showTreatmentPlan && (
          <button
            onClick={handleTreatmentPlan}
            className="w-full medical-button-secondary flex items-center justify-center space-x-2"
          >
            <FileText className="w-4 h-4" />
            <span>Treatment Plan</span>
          </button>
        )}
        
        {/* Smart Scan Button */}
        {featureConfig.showSmartScan && (
          <button
            onClick={handleScanAnalysis}
            className="w-full medical-button-secondary flex items-center justify-center space-x-2"
          >
            <Scan className="w-4 h-4" />
            <span>Smart Scan</span>
          </button>
        )}
        
        {/* Deep Research Button */}
        {featureConfig.showDeepResearch && handleDeepResearch && (
          <button
            onClick={handleDeepResearch}
            className="w-full medical-button-secondary flex items-center justify-center space-x-2"
          >
            <Search className="w-4 h-4" />
            <span>Deep Research</span>
          </button>
        )}
        
        {/* Treatment Demo Button */}
        {featureConfig.showTreatmentDemo && handleTreatmentDemo && (
          <button
            onClick={handleTreatmentDemo}
            className="w-full bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 flex items-center justify-center space-x-2 transition-colors"
          >
            <FileText className="w-4 h-4" />
            <span>Treatment Demo</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
